/**
 * Bulk inserter for auth-status-change comments via Postgres.
 * @module bulkInsertAuthStatusComments
 */

import { AuthStatusChangeComments } from "../../../data-model/authStatusChangeComments";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { getBearerToken } from "../../postCall/bearerToken";

/** Parameters for bulk-inserting auth-status-change comments. */
export interface BulkInsertAuthStatusCommentsParams {
  /** PA order ID the comments belong to. */
  order_id: string;
  /** Organisation scoping the insert. */
  org_id: string;
  /** Array of auth-status-change comment records to insert. */
  data: AuthStatusChangeComments[];
}

/** Response shape returned by the bulk comment insert endpoint. */
export interface BulkInsertAuthStatusCommentsResponse {
  success: boolean;
  inserted_rows: number;
}

/**
 * Sends a bulk insert request for auth-status-change comments to Postgres.
 *
 * @param params - Order ID, org ID, and the comment records to insert.
 * @param signal - Optional AbortSignal to cancel the in-flight request.
 * @returns The insert result indicating success and number of inserted rows.
 * @throws Re-throws any network or parsing error after logging.
 */
export const bulkInsertAuthStatusComments = async (
  params: BulkInsertAuthStatusCommentsParams,
  signal?: AbortSignal,
): Promise<BulkInsertAuthStatusCommentsResponse> => {
  try {
    const url = API_ENDPOINTS.AUTH_STATUS_CHANGE_COMMENTS;
    const bearerToken = await getBearerToken();
    const raw = JSON.stringify({
      order_id: params.order_id,
      org_id: params.org_id,
      data: params.data,
    });
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: raw,
      ...(signal && { signal }),
    });
    const result: BulkInsertAuthStatusCommentsResponse = await response.json();
    return result;
  } catch (error: any) {
    if (error.name !== "AbortError") {
      console.error(error);
    }
    throw error;
  }
};
